import React, { useEffect, useState, useCallback } from 'react';
import api from '../services/api';

const API_BASE = api.defaults.baseURL.replace(/\/api$/, '');
import { format } from 'date-fns';

const MONTHS = ['January','February','March','April','May','June',
                'July','August','September','October','November','December'];

export default function PLI() {
  const now = new Date();
  const [selYear,  setSelYear]  = useState(now.getFullYear());
  const [selMonth, setSelMonth] = useState(now.getMonth()); // 0-indexed
  const [data,      setData]      = useState([]);
  const [workingDays, setWorkingDays] = useState(0);
  const [loading,   setLoading]   = useState(false);
  const [search,    setSearch]    = useState('');
  const [exporting, setExporting] = useState(false);

  const years = [];
  for (let y = 2024; y <= now.getFullYear(); y++) years.push(y);

  const availableMonths = MONTHS.map((m, i) => ({ label: m, value: i }))
    .filter(m => selYear < now.getFullYear() || m.value <= now.getMonth());

  useEffect(() => {
    if (selYear === now.getFullYear() && selMonth > now.getMonth()) setSelMonth(now.getMonth());
  }, [selYear]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/pli?month=${selMonth + 1}&year=${selYear}`);
      setData(res.data.employees || []);
      setWorkingDays(res.data.working_days || 0);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [selYear, selMonth]);

  useEffect(() => { load(); }, [load]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(
        `${API_BASE}/api/pli/export?month=${selMonth + 1}&year=${selYear}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) { alert('Export failed'); return; }
      const blob = await res.blob();
      const url  = window.URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href = url;
      a.download = `PLI_${MONTHS[selMonth]}_${selYear}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch { alert('Export error'); }
    finally { setExporting(false); }
  };

  const filtered = data.filter(e =>
    !search ||
    e.name.toLowerCase().includes(search.toLowerCase()) ||
    String(e.employee_code).includes(search)
  );

  const totalIncentive   = filtered.reduce((s,e) => s + e.incentive_received, 0);
  const totalMonthlyPool = filtered.reduce((s,e) => s + e.monthly_incentive, 0);
  const avgUtilization   = filtered.length > 0
    ? filtered.reduce((s,e) => s + e.utilization_pct, 0) / filtered.length
    : 0;

  const utilColor = (pct) => {
    if (pct >= 100) return '#10b981';
    if (pct >= 80)  return '#f59e0b';
    if (pct >= 50)  return '#ef4444';
    return '#6b7280';
  };

  return (
    <div>
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">PLI — Performance Linked Incentive</h1>
          <p className="page-sub">{MONTHS[selMonth]} {selYear} — Incentive calculated from monthly utilization</p>
        </div>
        <button className="btn btn-primary" disabled={exporting || filtered.length===0} onClick={handleExport}>
          {exporting ? 'Exporting…' : '📥 Export Excel'}
        </button>
      </div>

      {/* Filters */}
      <div className="card" style={{ padding:'14px 18px', marginBottom:18, display:'flex', gap:12, alignItems:'center', flexWrap:'wrap' }}>
        <div className="field" style={{ flex:'0 0 auto', marginBottom:0 }}>
          <label>Year</label>
          <select value={selYear} onChange={e => setSelYear(Number(e.target.value))} style={{ minWidth:90 }}>
            {years.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
        <div className="field" style={{ flex:'0 0 auto', marginBottom:0 }}>
          <label>Month</label>
          <select value={selMonth} onChange={e => setSelMonth(Number(e.target.value))} style={{ minWidth:130 }}>
            {availableMonths.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
          </select>
        </div>
        <div style={{ flex:1, minWidth:200 }}>
          <label style={{ fontSize:12, color:'#9ca3af', display:'block', marginBottom:6 }}>Search</label>
          <input
            className="search-box"
            style={{ width:'100%', maxWidth:'none' }}
            placeholder="Name or employee code…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        <div style={{ marginTop:18, fontSize:13, color:'#6b7280' }}>
          {filtered.length} employees · {workingDays} working days
        </div>
      </div>

      {loading ? (
        <div className="loading">Calculating PLI…</div>
      ) : (
        <>
          {/* Table */}
          {filtered.length === 0 ? (
            <div className="card"><div className="empty"><div className="empty-icon">💰</div><p>No client-approved timesheets found for this month.</p></div></div>
          ) : (
            <div className="card" style={{ padding:0, overflow:'hidden' }}>
              <div style={{ overflowX:'auto' }}>
                <table>
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Employee Code</th>
                      <th>Name</th>
                      <th>Joining Date</th>
                      <th>Department</th>
                      <th>Monthly Incentive</th>
                      <th>Utilization %</th>
                      <th>Incentive Received</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered
                      .sort((a,b) => b.incentive_received - a.incentive_received)
                      .map((emp, idx) => (
                      <tr key={emp.employee_id}>
                        <td style={{ color:'#6b7280', fontSize:12 }}>{idx+1}</td>
                        <td>
                          <span style={{ fontSize:12.5, color:'#6c63ff', fontWeight:600 }}>#{emp.employee_code}</span>
                        </td>
                        <td>
                          <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                            <div className="avatar" style={{ width:28, height:28, fontSize:11 }}>{emp.name.charAt(0)}</div>
                            <span style={{ fontWeight:600, fontSize:13 }}>{emp.name}</span>
                          </div>
                        </td>
                        <td className="text-sm text-muted">
                          {emp.joining_date ? format(new Date(emp.joining_date), 'MMM d, yyyy') : '—'}
                        </td>
                        <td className="text-sm text-muted">{emp.department || '—'}</td>
                        <td style={{ textAlign:'center', fontSize:13 }}>₹{emp.monthly_incentive.toLocaleString('en-IN')}</td>
                        <td style={{ textAlign:'center' }}>
                          <span style={{ fontWeight:700, fontSize:13, color: utilColor(emp.utilization_pct) }}>
                            {emp.utilization_pct.toFixed(2)}%
                          </span>
                        </td>
                        <td style={{ textAlign:'center', fontWeight:700, fontSize:14, color:'#10b981' }}>
                          ₹{emp.incentive_received.toLocaleString('en-IN',{maximumFractionDigits:2})}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr style={{ borderTop:'2px solid #2a2d3a', background:'#111827' }}>
                      <td colSpan={5} style={{ fontWeight:700, fontSize:13, padding:'12px', color:'#fff' }}>TOTAL</td>
                      <td style={{ textAlign:'center', fontWeight:700 }}>₹{totalMonthlyPool.toLocaleString('en-IN')}</td>
                      <td style={{ textAlign:'center', fontWeight:700, color: utilColor(avgUtilization) }}>{avgUtilization.toFixed(2)}%</td>
                      <td style={{ textAlign:'center', fontWeight:700, color:'#10b981', fontSize:15 }}>₹{totalIncentive.toLocaleString('en-IN',{maximumFractionDigits:2})}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}